import { useRouter } from 'next/router';

import {
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Heading,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import { RentalManagerFreemiumAnalyticsFilterType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_comps,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_inquiries,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_prices,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_pricingGuidance,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { getCookieAsBoolean } from '../../../utils/cookieUtils';
import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { ZRM_USE_FREEMIUM_TEST_DATA } from '../../../constants/FeatureFlagConstants';

import RentPricingChart from '../../PremiumAnalytics/components/RentPricingChart';

import { usePremiumAnalyticsContext } from '../../PremiumAnalytics/PremiumAnalyticsContext';

const pmrResultsBaseUrl = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

const PremiumPricingGuidanceModal = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { filters } = usePremiumAnalyticsContext();
  const { beds, filterType, propertyType } = filters;

  const useTestData = getCookieAsBoolean(ZRM_USE_FREEMIUM_TEST_DATA);

  const { data: overviewData } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview(
    {
      listingAlias,
      filterType: RentalManagerFreemiumAnalyticsFilterType.CITY_STATE,
      beds: undefined,
      propertyType: undefined,
    },
    { enabled: !!listingAlias },
  );

  const {
    data: inquiriesData,
    error: InquiriesDataError,
    refetch: refetchGetListingPerformance,
  } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_inquiries(
    { listingAlias, filterType, bedrooms: beds, propertyType, useTestData },
    {
      enabled: !!listingAlias,
      placeholderData: (previousData) => previousData,
    },
  );

  const {
    data: pricingGuidance,
    error: pricingGuidanceError,
    refetch: refetchGetPricingGuidance,
  } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_pricingGuidance(
    { listingAlias, useTestData },
    { enabled: !!listingAlias },
  );

  const {
    data: compsData,
    error: compsDataError,
    refetch: refetchGetComps,
  } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_comps(
    { listingAlias, bedrooms: beds, filterType, propertyType, useTestData },
    {
      enabled: !!listingAlias,
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: pricingData } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_prices(
    { listingAlias, bedrooms: beds, filterType, propertyType, useTestData },
    {
      enabled: !!listingAlias,
      placeholderData: (previousData) => previousData,
    },
  );

  if (!overviewData || !pricingData || !compsData || !inquiriesData) {
    return null;
  }

  const handleCompsButtonClick = () => window.open(overviewData.compsUrl, '_blank', 'noopener, noreferrer');

  const { formattedKebobCase: pmrResultsAddress } = propertyUtils.formatAddress(overviewData);
  const pmrUrl = new URL(`${pmrResultsBaseUrl}${pmrResultsAddress}/`);
  overviewData?.unit && pmrUrl?.searchParams && pmrUrl.searchParams.set('unit', overviewData.unit);

  const { cityState, days, hasEnoughListings, hasListings } = compsData || {};
  const percentChangeValue = cityState?.percentChange;

  const getMedianPriceHeading = () => {
    if (pricingGuidanceError || !pricingGuidance) {
      return (
        <Text>
          We are having issues loading your pricing guidance data.{' '}
          <Anchor href="#" asChild>
            <button onClick={() => refetchGetPricingGuidance()}>Retry now</button>
          </Anchor>
          .
        </Text>
      );
    }
    const { beds: guidanceBeds, marketMedian, propertyType: guidancePropertyType, zip, city } = pricingGuidance;
    const medianPrice = marketMedian?.price;
    const propertyTypeDisplay = guidancePropertyType?.display;

    if ((guidanceBeds || guidanceBeds === 0) && propertyTypeDisplay && zip && medianPrice) {
      if (guidanceBeds === 0) {
        return `The median rental price for studios in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      }
      return `The median rental price for ${guidanceBeds}-bedroom ${propertyTypeDisplay.toLowerCase()}s in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
    }

    return `Optimize your listing with ${city} pricing data and insights`;
  };

  const getMarketInquiriesHeading = () => {
    if (InquiriesDataError) {
      return (
        <Text>
          We are having issues loading your market activity data.{' '}
          <TextButton onClick={() => refetchGetListingPerformance()}>Retry now</TextButton>
        </Text>
      );
    }

    const { marketAverage } = inquiriesData || {};
    const countSinceActivation = marketAverage?.countSinceActivation || 0;

    let inquiresInFirst10Days = 0;
    for (let i = 0; i < 10; i++) {
      inquiresInFirst10Days += marketAverage?.dailyCounts[i]?.count;
    }

    if (inquiresInFirst10Days && countSinceActivation) {
      const percentOfInquiries = Math.round((inquiresInFirst10Days / countSinceActivation) * 100);
      return `Most listings receive ${percentOfInquiries}% of their inquiries within the first 10 days`;
    }

    return 'Most listings receive a high volume of inquiries within the first 10 days';
  };

  const getInventoryCardHeading = () => {
    if (compsDataError) {
      return (
        <Text>
          We are having issues loading your market comparison data.{' '}
          <TextButton onClick={() => refetchGetComps()}>Retry now</TextButton>
        </Text>
      );
    }

    let comparativeValue;

    if (!hasEnoughListings) {
      return `In the last 14 days, there have been ${hasListings ? 'very few' : '0'} rentals listed in your market`;
    }

    if (percentChangeValue && percentChangeValue > 0) {
      comparativeValue = `increased by ${percentChangeValue}%`;
    }
    if (percentChangeValue && percentChangeValue < 0) {
      comparativeValue = `decreased by ${Math.abs(percentChangeValue)}%`;
    }

    if (percentChangeValue === 0) {
      comparativeValue = 'stayed the same';
    }

    return `In the last ${days} days, the inventory in your market
    has ${comparativeValue}`;
  };

  return (
    <Modal
      data-testid="PremiumPricingGuidanceModal"
      multiModalTriggerId="PremiumPricingGuidanceModal"
      size={{ base: 'fullScreen', lg: 'xl' }}
      header={<Heading level={2}>Rent pricing insights</Heading>}
      trigger={null}
      body={
        <>
          <RentPricingChart listingAlias={listingAlias} isInModal={true} />
          <Divider css={{ marginTop: '600' }} />
          <Box
            css={{
              paddingBlock: '600',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { base: 'column', lg: 'row' },
            }}
          >
            <Card elevated={false} tone="neutral" css={{ margin: '200', flexGrow: 1, flexBasis: 0 }}>
              <Heading level={3} textStyle="body-lg-bold">
                {getMedianPriceHeading()}
              </Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                Review your comparable listings. How do they compare to your property in terms of condition and
                amenities? These resources may help you decide:
              </Paragraph>
              {pricingData?.hasActiveComps && (
                <Button
                  tone="brand"
                  emphasis="outlined"
                  size="sm"
                  onClick={handleCompsButtonClick}
                  css={{ marginBottom: '400' }}
                >
                  View comparable listings
                </Button>
              )}
            </Card>
            <Card elevated={false} tone="neutral" css={{ margin: '200', flexGrow: 1, flexBasis: 0 }}>
              <Heading level={3} textStyle="body-lg-bold">
                {getMarketInquiriesHeading()}
              </Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                Your first impression on the market is important. Rentals priced about the same as or a bit lower than
                their competition are likely to get the most interest.
              </Paragraph>
              <Paragraph textStyle="body-sm" css={{ marginBottom: '400' }}>
                Consider your goals for price vs. time on market when you&apos;re setting your price.
              </Paragraph>
              {pmrUrl && (
                <TextButton asChild textStyle="body-sm">
                  <a href={pmrUrl.toString()} target="_blank" rel="noopener noreferrer">
                    Explore the pricing tool
                  </a>
                </TextButton>
              )}
            </Card>
            <Card elevated={false} tone="neutral" css={{ margin: '200', flexGrow: 1, flexBasis: 0 }}>
              <Heading level={3} textStyle="body-lg-bold">
                {getInventoryCardHeading()}
              </Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                This can impact the average rental price in your market.
              </Paragraph>
            </Card>
          </Box>
        </>
      }
      footer={
        <Modal.Close>
          <Button size="md" tone="brand" emphasis="filled">
            Done
          </Button>
        </Modal.Close>
      }
    />
  );
};

export default PremiumPricingGuidanceModal;
